/**
 * EVG ring size guide (sections/evg-size-guide.liquid).
 * Every measurement becomes a position on the Italian scale (6–23): diameters
 * follow the variant labels ("13 (16.8 mm)"), circumferences are size + 40 mm.
 * Sizes and copy come from the JSON the section renders.
 */

const CARD_SHORT_SIDE_MM = 53.98;
const CARD_LONG_SIDE_MM = 85.6;
const CARD_RADIUS_MM = 3.18;
const SCALE_STORAGE_KEY = 'evg-size-guide:px-per-mm';
const UK_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const US_FRACTIONS = ['', '¼', '½', '¾'];

class EvgSizeGuide extends HTMLElement {
  /** @type {{it: number, d: number, label: string}[]} */
  sizes = [];

  /** @type {Record<string, string>} */
  copy = {};

  connectedCallback() {
    this.sizes = this.#readJson('[data-evg-sizes]') ?? [];
    this.copy = this.#readJson('[data-evg-strings]') ?? {};
    if (!this.sizes.length) return;

    this.locale = document.documentElement.lang || 'it';
    this.#setupConverter();
    this.#setupTabs();
    this.#setupScreenSizer();
    this.#setupMeasureInputs();
    this.#setupPrint();
  }

  /**
   * @param {string} selector
   */
  #readJson(selector) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent ?? '');
    } catch {
      return null;
    }
  }

  /* Scale ------------------------------------------------------------------ */

  /**
   * Position on the Italian scale from an inner diameter in mm.
   * @param {number} diameter
   */
  #positionFromDiameter(diameter) {
    const first = this.sizes[0];
    const last = this.sizes[this.sizes.length - 1];
    // About 0.32 mm of diameter per size outside the table.
    if (diameter <= first.d) return first.it - (first.d - diameter) / 0.32;
    if (diameter >= last.d) return last.it + (diameter - last.d) / 0.32;

    for (let i = 0; i < this.sizes.length - 1; i++) {
      const lower = this.sizes[i];
      const upper = this.sizes[i + 1];
      if (diameter <= upper.d) return lower.it + (diameter - lower.d) / (upper.d - lower.d);
    }
    return NaN;
  }

  /**
   * Position on the Italian scale from an inner circumference in mm.
   * @param {number} circumference
   */
  #positionFromCircumference(circumference) {
    return circumference - 40;
  }

  /**
   * @param {number} position
   * @returns {{kind: 'exact', size: number} | {kind: 'between', lo: number, hi: number} | {kind: 'below' | 'above'} | null}
   */
  #describe(position) {
    if (!Number.isFinite(position)) return null;

    const min = this.sizes[0].it;
    const max = this.sizes[this.sizes.length - 1].it;
    if (position < min - 0.4) return { kind: 'below' };
    if (position > max + 0.4) return { kind: 'above' };

    const lo = Math.floor(position);
    const fraction = position - lo;
    if (fraction <= 0.3 || lo + 1 > max) return { kind: 'exact', size: Math.max(min, Math.min(max, lo)) };
    if (fraction >= 0.7 || lo < min) return { kind: 'exact', size: Math.max(min, Math.min(max, lo + 1)) };
    return { kind: 'between', lo, hi: lo + 1 };
  }

  /* Rendering -------------------------------------------------------------- */

  /**
   * @param {number} value
   * @param {number} digits
   */
  #format(value, digits) {
    return new Intl.NumberFormat(this.locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);
  }

  /**
   * @param {string} template
   * @param {Record<string, string | number>} values
   */
  #fill(template = '', values = {}) {
    return template.replace(/\{(\w+)\}/g, (match, key) => (key in values ? String(values[key]) : match));
  }

  /**
   * @param {number} it
   */
  #size(it) {
    return this.sizes.find((size) => size.it === it);
  }

  /**
   * @param {Element} target
   * @param {string} message
   */
  #renderMessage(target, message) {
    const paragraph = document.createElement('p');
    paragraph.className = 'evg-sg__result-empty';
    paragraph.textContent = message;
    target.replaceChildren(paragraph);
    target.classList.remove('is-filled');
  }

  /**
   * @param {Element} target
   * @param {number} position
   * @param {{highlight?: boolean}} [options]
   */
  #renderResult(target, position, { highlight = true } = {}) {
    const result = this.#describe(position);
    if (!result) {
      this.#renderMessage(target, this.copy.empty);
      return;
    }

    let badge = '';
    let title = '';
    const lines = [];

    if (result.kind === 'exact') {
      const size = this.#size(result.size);
      badge = String(result.size);
      title = this.#fill(this.copy.exact, { size: result.size });
      if (size) {
        lines.push(this.#fill(this.copy.detail, { d: this.#format(size.d, 1), c: size.it + 40 }));
        lines.push(this.#fill(this.copy.pick, { label: size.label }));
      }
    } else if (result.kind === 'between') {
      badge = `${result.lo}–${result.hi}`;
      title = this.#fill(this.copy.between, result);
      lines.push(this.#fill(this.copy.betweenHint, result));
    } else {
      badge = result.kind === 'below' ? `< ${this.sizes[0].it}` : `> ${this.sizes[this.sizes.length - 1].it}`;
      title = result.kind === 'below' ? this.copy.below : this.copy.above;
      lines.push(this.copy.custom);
    }

    const badgeElement = document.createElement('span');
    badgeElement.className = 'evg-sg__badge';
    badgeElement.dataset.kind = result.kind;
    badgeElement.textContent = badge;

    const copyElement = document.createElement('div');
    copyElement.className = 'evg-sg__result-copy';

    const titleElement = document.createElement('p');
    titleElement.className = 'evg-sg__result-title';
    titleElement.textContent = title;
    copyElement.append(titleElement);

    for (const line of lines) {
      const lineElement = document.createElement('p');
      lineElement.className = 'evg-sg__result-detail';
      lineElement.textContent = line;
      copyElement.append(lineElement);
    }

    target.replaceChildren(badgeElement, copyElement);
    target.classList.add('is-filled');

    if (highlight) this.#highlightRows(result);
  }

  /**
   * @param {ReturnType<EvgSizeGuide['#describe']>} result
   */
  #highlightRows(result) {
    /** @type {number[]} */
    let matches = [];
    if (result?.kind === 'exact') matches = [result.size];
    if (result?.kind === 'between') matches = [result.lo, result.hi];

    for (const row of this.querySelectorAll('[data-size-row]')) {
      if (!(row instanceof HTMLElement)) continue;
      row.classList.toggle('is-match', matches.includes(Number(row.dataset.sizeRow)));
    }
  }

  /* Converter -------------------------------------------------------------- */

  /**
   * @param {string} system
   * @returns {{label: string, position: number}[]}
   */
  #systemOptions(system) {
    /** @type {{label: string, position: number}[]} */
    const options = [];

    if (system === 'it') {
      for (const size of this.sizes) options.push({ label: String(size.it), position: size.it });
    }

    if (system === 'd') {
      for (let tenth = 140; tenth <= 210; tenth++) {
        const diameter = tenth / 10;
        options.push({ label: `${this.#format(diameter, 1)} mm`, position: this.#positionFromDiameter(diameter) });
      }
    }

    if (system === 'c') {
      for (let mm = 44; mm <= 65; mm++) {
        options.push({ label: `${mm} mm`, position: this.#positionFromCircumference(mm) });
      }
    }

    if (system === 'us') {
      // US: inner diameter = 11.63 mm + 0.8128 mm per size.
      for (let quarter = 12; quarter <= 44; quarter++) {
        const size = quarter / 4;
        const whole = Math.floor(size);
        const diameter = 11.63 + 0.8128 * size;
        options.push({
          label: `${whole}${US_FRACTIONS[quarter % 4]}`,
          position: this.#positionFromCircumference(Math.PI * diameter),
        });
      }
    }

    if (system === 'uk') {
      // UK/AU: A = 37.8 mm circumference, 1.25 mm per letter.
      for (let index = UK_LETTERS.indexOf('F'); index <= UK_LETTERS.indexOf('V'); index++) {
        for (const half of [0, 1]) {
          const circumference = 37.8 + 1.25 * index + 0.625 * half;
          options.push({
            label: `${UK_LETTERS[index]}${half ? '½' : ''}`,
            position: this.#positionFromCircumference(circumference),
          });
        }
      }
    }

    return options;
  }

  #setupConverter() {
    const root = this.querySelector('[data-converter]');
    const systemSelect = root?.querySelector('[data-system]');
    const valueSelect = root?.querySelector('[data-value]');
    const result = root?.querySelector('[data-result]');
    if (!(systemSelect instanceof HTMLSelectElement) || !(valueSelect instanceof HTMLSelectElement) || !result) return;

    /** @type {{label: string, position: number}[]} */
    let options = [];

    const populate = () => {
      options = this.#systemOptions(systemSelect.value);
      const placeholder = new Option(this.copy.choose ?? '', '');
      valueSelect.replaceChildren(placeholder, ...options.map((option, index) => new Option(option.label, String(index))));
      this.#renderMessage(result, this.copy.empty);
    };

    systemSelect.addEventListener('change', populate);
    valueSelect.addEventListener('change', () => {
      const option = options[Number(valueSelect.value)];
      if (valueSelect.value === '' || !option) {
        this.#renderMessage(result, this.copy.empty);
        return;
      }
      this.#renderResult(result, option.position);
    });

    populate();
  }

  /* Tabs ------------------------------------------------------------------- */

  #setupTabs() {
    for (const tablist of this.querySelectorAll('[data-tabs]')) {
      const tabs = [...tablist.querySelectorAll('[role="tab"]')].filter((tab) => tab instanceof HTMLElement);

      /**
       * @param {HTMLElement} selected
       * @param {boolean} [focus]
       */
      const select = (selected, focus = false) => {
        for (const tab of tabs) {
          const isSelected = tab === selected;
          tab.setAttribute('aria-selected', String(isSelected));
          tab.tabIndex = isSelected ? 0 : -1;
          const panel = document.getElementById(tab.getAttribute('aria-controls') ?? '');
          if (panel) panel.hidden = !isSelected;
        }
        if (focus) selected.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (event) => {
          let next = -1;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next < 0) return;
          event.preventDefault();
          select(tabs[next], true);
        });
      });
    }
  }

  /* On-screen sizer -------------------------------------------------------- */

  #readScale() {
    try {
      const stored = Number(window.localStorage.getItem(SCALE_STORAGE_KEY));
      return stored > 0 ? stored : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {number} scale
   */
  #storeScale(scale) {
    try {
      window.localStorage.setItem(SCALE_STORAGE_KEY, String(scale));
    } catch {
      // Private mode or blocked storage: the calibration just isn't remembered.
    }
  }

  /** A first guess of CSS pixels per millimetre before calibration. */
  #guessScale() {
    if (window.matchMedia('(pointer: coarse)').matches) {
      return Math.min(window.screen.width, window.screen.height) < 600 ? 6.2 : 5.2;
    }
    return 96 / 25.4;
  }

  #setupScreenSizer() {
    const root = this.querySelector('[data-screen-sizer]');
    if (!root) return;

    const calibrateStep = root.querySelector('[data-step="calibrate"]');
    const ringStep = root.querySelector('[data-step="ring"]');
    const calibratedNote = root.querySelector('[data-calibrated]');
    const card = root.querySelector('[data-card]');
    const calibration = root.querySelector('[data-calib]');
    const ring = root.querySelector('[data-ring]');
    const diameterInput = root.querySelector('[data-ring-diameter]');
    const value = root.querySelector('[data-ring-value]');
    const result = root.querySelector('[data-result]');
    if (
      !(calibrateStep instanceof HTMLElement) ||
      !(ringStep instanceof HTMLElement) ||
      !(card instanceof HTMLElement) ||
      !(calibration instanceof HTMLInputElement) ||
      !(ring instanceof HTMLElement) ||
      !(diameterInput instanceof HTMLInputElement) ||
      !value ||
      !result
    ) {
      return;
    }

    const min = Number(calibration.min);
    const max = Number(calibration.max);
    const storedScale = this.#readScale();
    let scale = Math.min(max, Math.max(min, storedScale ?? this.#guessScale()));
    let touched = false;

    /**
     * Calibration first, then the ring; a calibration saved on this device skips step 1.
     * @param {'calibrate' | 'ring'} step
     */
    const showStep = (step) => {
      calibrateStep.hidden = step !== 'calibrate';
      ringStep.hidden = step !== 'ring';
    };

    const applyRing = () => {
      const diameter = Number(diameterInput.value);
      ring.style.width = `${diameter * scale}px`;
      ring.style.height = `${diameter * scale}px`;
      const label = this.#fill(this.copy.screenValue, { d: this.#format(diameter, 1) });
      value.textContent = label;
      diameterInput.setAttribute('aria-valuetext', label);
      this.#renderResult(result, this.#positionFromDiameter(diameter), { highlight: touched });
    };

    const applyScale = () => {
      calibration.value = String(scale);
      card.style.width = `${CARD_SHORT_SIDE_MM * scale}px`;
      card.style.height = `${CARD_LONG_SIDE_MM * scale}px`;
      card.style.borderRadius = `${CARD_RADIUS_MM * scale}px`;
      applyRing();
    };

    /**
     * @param {number} next
     */
    const setScale = (next) => {
      scale = Math.min(max, Math.max(min, next));
      applyScale();
    };

    calibration.addEventListener('input', () => setScale(Number(calibration.value)));
    for (const button of root.querySelectorAll('[data-calib-step]')) {
      if (!(button instanceof HTMLElement)) continue;
      button.addEventListener('click', () => setScale(scale + Number(button.dataset.calibStep) * 0.02));
    }

    root.querySelector('[data-go-ring]')?.addEventListener('click', () => {
      this.#storeScale(scale);
      if (calibratedNote instanceof HTMLElement) calibratedNote.hidden = true;
      showStep('ring');
      ringStep.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    root.querySelector('[data-recalibrate]')?.addEventListener('click', () => {
      showStep('calibrate');
      calibrateStep.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });

    diameterInput.addEventListener('input', () => {
      touched = true;
      applyRing();
    });
    for (const button of root.querySelectorAll('[data-ring-step]')) {
      if (!(button instanceof HTMLElement)) continue;
      // Fine steps of 0.1 mm: the ring is found by growing the circle until the light strip disappears.
      button.addEventListener('click', () => {
        const next = Number(diameterInput.value) + Number(button.dataset.ringStep) * 0.1;
        const clamped = Math.min(Number(diameterInput.max), Math.max(Number(diameterInput.min), next));
        touched = true;
        diameterInput.value = clamped.toFixed(2);
        applyRing();
      });
    }

    applyScale();
    showStep(storedScale ? 'ring' : 'calibrate');
    if (calibratedNote instanceof HTMLElement) calibratedNote.hidden = !storedScale;
  }

  /* Ruler and finger ------------------------------------------------------- */

  /**
   * @param {string} raw
   */
  #parseNumber(raw) {
    const cleaned = raw.replace(',', '.').replace(/[^\d.]/g, '');
    return cleaned ? Number.parseFloat(cleaned) : NaN;
  }

  #setupMeasureInputs() {
    for (const root of this.querySelectorAll('[data-measure]')) {
      if (!(root instanceof HTMLElement)) continue;

      const input = root.querySelector('[data-input]');
      const result = root.querySelector('[data-result]');
      if (!(input instanceof HTMLInputElement) || !result) continue;

      const isDiameter = root.dataset.measure === 'diameter';
      const invalid = this.#fill(this.copy.invalid, { example: root.dataset.example ?? '' });

      input.addEventListener('input', () => {
        const raw = input.value.trim();
        if (!raw) {
          this.#renderMessage(result, this.copy.empty);
          this.#highlightRows(null);
          return;
        }

        let measure = this.#parseNumber(raw);
        // Someone writing centimetres (1,68 or 5,3) instead of millimetres.
        if (isDiameter && measure > 0 && measure < 3) measure *= 10;
        if (!isDiameter && measure > 0 && measure < 10) measure *= 10;

        const plausible = isDiameter ? measure >= 10 && measure <= 30 : measure >= 30 && measure <= 100;
        if (!Number.isFinite(measure) || !plausible) {
          this.#renderMessage(result, invalid);
          return;
        }

        const position = isDiameter ? this.#positionFromDiameter(measure) : this.#positionFromCircumference(measure);
        this.#renderResult(result, position);
      });
    }
  }

  /* Printable sizer -------------------------------------------------------- */

  #setupPrint() {
    const button = this.querySelector('[data-print]');
    const template = this.querySelector('template[data-print-sheet]');
    if (!button || !(template instanceof HTMLTemplateElement)) return;

    button.addEventListener('click', () => {
      document.querySelector('.evg-print-root')?.remove();

      const root = document.createElement('div');
      root.className = 'evg-print-root';
      root.append(template.content.cloneNode(true));
      document.body.append(root);
      document.documentElement.classList.add('evg-print-mode');

      const cleanup = () => {
        root.remove();
        document.documentElement.classList.remove('evg-print-mode');
      };
      window.addEventListener('afterprint', cleanup, { once: true });
      window.print();
    });
  }
}

if (!customElements.get('evg-size-guide')) {
  customElements.define('evg-size-guide', EvgSizeGuide);
}
